import { SymbolImageCache, type PreparedSymbolCatalogue } from '../../symbols/images';
import { sceneSymbolRequests } from '../../symbols/scene';
import { SymbolImportError } from '../../symbols/model';
import { downloadBrowserFile, type BrowserDownload } from '../../browser/download';
import { isCapabilityAvailable, type Entitlement } from '../../licensing/entitlements';
import { portableFilename } from '../../project/dropmap/filename';
import type { ProjectModelDraft } from '../../project/model';
import { deriveGroundFootprint } from '../inset';
import { withRenderedInset } from './insetRenderer';
import { encodePdf, encodePng } from './encoding';
import { withRenderedStaticMapFrame } from './mapRenderer';
import { type StaticExportRequest, StaticExportError } from './model';
import { renderCompositionPage } from './pageRenderer';
import { buildStaticCompositionPlan } from './planning';
import { staticBasemapAttributionText } from '../../basemaps/catalog';
import {
  nextPaint,
  OperationCancelledError,
  throwIfOperationCancelled,
  type LongOperationOptions,
  type LongOperationStage,
} from '../../operations/longOperation';

export type StaticExportStageId =
  | 'preparing'
  | 'rendering-map'
  | 'composing-page'
  | 'encoding-png'
  | 'rendering-basemap'
  | 'rendering-labels'
  | 'creating-vector-pdf'
  | 'creating-raster-pdf'
  | 'finalising';

export type StaticExportOptions = LongOperationOptions<StaticExportStageId> & {
  /** Current Composer map layout density; transient and never persisted. */
  readonly mapLogicalPixelsPerMm?: number;
  readonly symbolImages?: PreparedSymbolCatalogue;
};

const EXPORT_STAGES: Readonly<
  Record<StaticExportStageId, LongOperationStage<StaticExportStageId>>
> = {
  preparing: { id: 'preparing', label: 'Preparing export…' },
  'rendering-map': { id: 'rendering-map', label: 'Rendering map…' },
  'composing-page': { id: 'composing-page', label: 'Compositing page…' },
  'encoding-png': { id: 'encoding-png', label: 'Encoding PNG…' },
  'rendering-basemap': { id: 'rendering-basemap', label: 'Rendering basemap…' },
  'rendering-labels': { id: 'rendering-labels', label: 'Rendering map labels…' },
  'creating-vector-pdf': { id: 'creating-vector-pdf', label: 'Creating vector PDF…' },
  'creating-raster-pdf': { id: 'creating-raster-pdf', label: 'Creating raster PDF…' },
  finalising: { id: 'finalising', label: 'Finalising export…' },
};

async function announceStage(
  id: StaticExportStageId,
  options: StaticExportOptions,
): Promise<number> {
  throwIfOperationCancelled(options.signal);
  options.onStage?.(EXPORT_STAGES[id]);
  await nextPaint(options.signal);
  performance.mark(`dropmap-export:${id}:start`);
  return performance.now();
}

function finishStage(id: StaticExportStageId, started: number, options: StaticExportOptions): void {
  options.onStageTiming?.(id, performance.now() - started);
  performance.measure(`dropmap-export:${id}`, `dropmap-export:${id}:start`);
  performance.clearMarks(`dropmap-export:${id}:start`);
  throwIfOperationCancelled(options.signal);
}

async function prepareResolvedStaticMapExport(
  project: ProjectModelDraft,
  request: StaticExportRequest,
  entitlement: Entitlement,
  options: StaticExportOptions = {},
): Promise<BrowserDownload> {
  if (!isCapabilityAvailable(entitlement, 'export.static')) {
    throw new StaticExportError('download', 'Static map export requires Placeio Maps Pro.');
  }
  const preparing = await announceStage('preparing', options);
  finishStage('preparing', preparing, options);
  if (request.format === 'pdf-vector') {
    const { createVectorPdf } = await import('./vectorPdf/document');
    return {
      blob: await createVectorPdf(project, request.dpi, options),
      filename: portableFilename(project.metadata.title, 'pdf', 'Placeio Maps map'),
    };
  }
  const plan = buildStaticCompositionPlan(
    project.composition,
    project.sources,
    project.layers,
    request.dpi,
    'raster',
    staticBasemapAttributionText(project.map.basemap),
    options.mapLogicalPixelsPerMm,
    project.annotationLayers,
  );
  const renderingMap = await announceStage('rendering-map', options);
  const png = await withRenderedStaticMapFrame<Blob>(
    plan,
    project.map.backgroundColour,
    project.sources,
    project.layers,
    async (mapCanvas, map, releaseMap) => {
      finishStage('rendering-map', renderingMap, options);
      const composing = await announceStage('composing-page', options);
      // Capture once, then retire the large WebGL context before allocating the page or inset.
      const footprint = plan.insetMap
        ? deriveGroundFootprint(
            plan.mapFrame.logicalViewport.widthPx,
            plan.mapFrame.logicalViewport.heightPx,
            map.getCenter().lng,
            (point) => map.unproject([...point]),
          )
        : null;
      const mainZoom = plan.insetMap ? map.getZoom() : plan.mapFrame.view.zoom;
      let snapshot =
        typeof createImageBitmap === 'function' ? await createImageBitmap(mapCanvas) : null;
      if (snapshot) releaseMap();
      let page: HTMLCanvasElement | null = null;
      try {
        page = plan.insetMap
          ? await withRenderedInset(
              plan,
              project,
              footprint,
              (insetCapture) =>
                renderCompositionPage(plan, snapshot ?? mapCanvas, {
                  insetCapture,
                  ...(options.symbolImages ? { symbolImages: options.symbolImages } : {}),
                }),
              {
                mainZoom,
                symbolAssets: project.symbolAssets,
                ...(options.symbolImages ? { symbolImages: options.symbolImages } : {}),
                ...(options.signal ? { signal: options.signal } : {}),
              },
            )
          : await renderCompositionPage(
              plan,
              snapshot ?? mapCanvas,
              options.symbolImages ? { symbolImages: options.symbolImages } : {},
            );
        // Once copied into the page, the map snapshot no longer needs a full RGBA surface.
        snapshot?.close();
        snapshot = null;
        finishStage('composing-page', composing, options);
        // The fallback without ImageBitmap still releases immediately after readback.
        releaseMap();
        const encoding = await announceStage('encoding-png', options);
        const blob = await encodePng(page);
        finishStage('encoding-png', encoding, options);
        return blob;
      } finally {
        snapshot?.close();
        if (page) {
          page.width = 1;
          page.height = 1;
        }
      }
    },
    {
      symbolAssets: project.symbolAssets,
      ...(options.symbolImages ? { symbolImages: options.symbolImages } : {}),
      basemapConfiguration: project.map.basemap,
      annotationLayers: project.annotationLayers,
      activeFocusAreaId: project.activeFocusAreaId,
      ...(options.signal ? { signal: options.signal } : {}),
    },
  );
  const extension = request.format;
  const safeName = portableFilename(project.metadata.title, extension, 'Placeio Maps map');
  const filename = request.format === 'pdf' ? `${safeName.slice(0, -4)}-raster.pdf` : safeName;
  if (request.format === 'png') {
    const finalising = await announceStage('finalising', options);
    finishStage('finalising', finalising, options);
    return { blob: png, filename };
  }
  const pdf = await announceStage('creating-raster-pdf', options);
  const blob = await encodePdf(png, plan.page.widthMm, plan.page.heightMm, project.metadata.title);
  finishStage('creating-raster-pdf', pdf, options);
  const finalising = await announceStage('finalising', options);
  finishStage('finalising', finalising, options);
  return {
    blob,
    filename,
  };
}

export async function prepareStaticMapExport(
  project: ProjectModelDraft,
  request: StaticExportRequest,
  entitlement: Entitlement,
  options: StaticExportOptions = {},
): Promise<BrowserDownload> {
  if (!isCapabilityAvailable(entitlement, 'export.static'))
    throw new StaticExportError('download', 'Static map export requires Placeio Maps Pro.');
  const cache = new SymbolImageCache();
  try {
    const symbolImages =
      options.symbolImages ??
      (await cache.graphics(
        sceneSymbolRequests(project.layers, project.annotationLayers),
        project.symbolAssets,
        Math.max(2, request.dpi / 96),
        options.signal,
      ));
    return await prepareResolvedStaticMapExport(project, request, entitlement, {
      ...options,
      symbolImages,
    });
  } catch (cause) {
    throwIfOperationCancelled(options.signal);
    if (cause instanceof SymbolImportError)
      throw new StaticExportError('image', cause.message, { cause });
    throw cause;
  } finally {
    cache.clear();
  }
}

export async function exportStaticMap(
  project: ProjectModelDraft,
  request: StaticExportRequest,
  entitlement: Entitlement,
  options: StaticExportOptions = {},
): Promise<string> {
  try {
    const file = await prepareStaticMapExport(project, request, entitlement, options);
    throwIfOperationCancelled(options.signal);
    return downloadBrowserFile(file);
  } catch (cause) {
    if (cause instanceof StaticExportError || cause instanceof OperationCancelledError) throw cause;
    throw new StaticExportError('download', 'Could not download the static map export.', { cause });
  }
}
